import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    BreadcrumbGroup,
    SpaceBetween,
    Button,
    ContentLayout,
    Box,
    Container,
    Header,
    FormField,
    Input,
    RadioGroup,
    Select,
    DatePicker,
    TimeInput,
    ColumnLayout,
    Alert,
    StatusIndicator,
    Link,
    ExpandableSection,
    Textarea,
    Toggle,
    Grid,
  Icon,
  ProgressBar,
  Spinner, Badge
} from '@amzn/awsui-components-console';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { getUserAtom } from '../../contexts/userContext';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { useProfile } from '../../hooks/useProfiles';
import { jobUpdateSchema, validateWithSchema } from '../../utils/validationSchemas';
import JsonDataViewer from '../molecules/JsonDataViewer';
import NProgress from 'nprogress';
import { useFeedbackGeneration } from '../../hooks/useFeedbackGeneration';
import './JobCreateEdit.scss';

interface JobFormData {
    name: string;
    type: 'ondemand' | 'scheduled';
    scheduleFrequency?: 'monthly' | 'daily' | 'weekly' | 'custom';
    scheduleTime?: string;
    scheduleDate?: string;
    description?: string;
    templateFile?: File | null;
    templateId?: string;
    enabled: boolean;
    status?: 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'RELEASE';
}

interface JobDetails {
    jobId: string;
    jobName: string;
    description?: string;
    profileId: string;
    status: 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'RELEASE';
    templateId?: string;
    templateUri?: string;  // S3 URI of the template
    templateStatus?: string;  // Template validation status
    mermaidDiagram?: string;  // Mermaid diagram from GetJob API
    aiSummary?: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
}

const JobCreateEdit: React.FC = () => {
    const { id: profileId, jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [authenticatedUser] = useAtom(getUserAtom);
    
    // Fetch profile data from API
    const { profile: profileData, loading: profileLoading, error: profileError } = useProfile(profileId || null);
    
    // Determine if we're in edit mode
    const isEditMode = Boolean(jobId);
    const pageTitle = isEditMode ? 'Edit Job' : 'Create Job';
    
    // Form state
    const [formData, setFormData] = useState<JobFormData>({
        name: '',
        type: 'ondemand',
        scheduleFrequency: 'monthly',
        scheduleTime: '00:00',
        scheduleDate: '',
        description: '',
        templateFile: null,
        templateId: '',
        enabled: true,
        status: 'DRAFT'
    });
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState<string | null>(null);
    const [templateUploaded, setTemplateUploaded] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [jobDetails, setJobDetails] = useState<JobDetails | null>(null);
    const [templatePreviewData, setTemplatePreviewData] = useState<any>(null);
    const [isRunningJob, setIsRunningJob] = useState(false);
    
    // Feedback generation hook
    const {
        feedbackText,
        setFeedbackText,
        wordCount,
        isGenerating: isFeedbackGenerating,
        generationStatus: feedbackGenerationStatus,
        generationProgress,
        submitFeedback,
        error: feedbackError,
        clearError: clearFeedbackError,
        successMessage: feedbackSuccessMessage,
        clearSuccessMessage: clearFeedbackSuccessMessage
    } = useFeedbackGeneration({
        jobId: jobId || '',
        onSuccess: (templateStatus) => {
            // Reload template data if available
            if (templateStatus.templateUri) {
                loadTemplatePreviewData(templateStatus.templateUri);
                // Update job details to reflect new template
                if (jobDetails) {
                    setJobDetails({
                        ...jobDetails,
                        templateUri: templateStatus.templateUri,
                        templateId: templateStatus.templateId
                    });
                }
            }
            // Refresh the entire job details to get the latest status
            fetchJobDetails();
        },
        onError: () => {
            // Also refresh on error to get the latest status
            fetchJobDetails();
        }
    });
    
    // Helper function to get feedback status message
    const getFeedbackStatusMessage = (status: string): string => {
        switch(status) {
            case 'generating':
                return 'Processing your feedback and generating improved template...';
            case 'polling':
                return '';
            case 'success':
                return 'Template successfully updated with your feedback!';
            case 'failed':
                return 'Template generation failed. Please try again.';
            default:
                return 'Preparing to generate template...';
        }
    };
    
    // Load template preview data from S3 using templateUri
    const loadTemplatePreviewData = useCallback(async (templateUri?: string) => {
        if (!templateUri) {
            return;
        }
        
        try {
            NProgress.start();
            
            // Get presigned URL for the template
            const { getPresignedDownloadUrl } = await import('../../utils/s3Download');
            const urlResponse = await getPresignedDownloadUrl(templateUri);
            
            if (!urlResponse?.downloadUrl) {
                console.error('Failed to get presigned URL for template');
                return;
            }
            
            // Fetch the template content
            const response = await fetch(urlResponse.downloadUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.statusText}`);
            }
            
            const templateData = await response.json();
            
            setTemplatePreviewData(templateData);
        } catch (error: any) {
            console.error('Error loading template preview:', error);
            setApiError(`Failed to load template preview: ${error.message}`);
        } finally {
            NProgress.done();
        }
    }, []);
    
    // Function to fetch job details
    const fetchJobDetails = useCallback(async () => {
            if (!isEditMode || !jobId || !authenticatedUser) return;
            
            setLoading(true);
            setApiError(null);
            NProgress.start();
            
            try {
                const response = await makeApiCall(
                    API_PATHS.JOBS.GET,
                    'POST',
                    {
                        userId: authenticatedUser.username,
                        jobId: jobId
                    }
                );
                
                if (response.job) {
                    const job = response.job;
                    setJobDetails(job);
                    
                    // Map job details to form data
                    setFormData({
                        name: job.jobName || '',
                        type: 'ondemand', // Default, as API doesn't provide this
                        scheduleFrequency: 'monthly',
                        scheduleTime: '00:00',
                        scheduleDate: '',
                        description: job.description || '',
                        templateFile: null,
                        templateId: job.templateId || '',
                        enabled: job.status === 'ACTIVE' || job.status === 'RELEASE',
                        status: job.status
                    });
                    
                    if (job.templateUri) {
                        setTemplateUploaded(true);
                        // Load template preview data from S3
                        loadTemplatePreviewData(job.templateUri);
                    }
                }
            } catch (error: any) {
                console.error('Failed to fetch job details:', error);
                setApiError(error.message || 'Failed to load job details');
            } finally {
                setLoading(false);
                NProgress.done();
            }
    }, [isEditMode, jobId, authenticatedUser, loadTemplatePreviewData]);
        
    // Load existing job data if in edit mode
    useEffect(() => {
        fetchJobDetails();
    }, [fetchJobDetails]);
    
    // Validate form with Zod
    const validateForm = (): boolean => {
        const validationData = {
            name: formData.name,
            description: formData.description,
            type: formData.type,
            scheduleTime: formData.scheduleTime,
            scheduleDate: formData.scheduleDate
        };
        
        const result = validateWithSchema(jobUpdateSchema, validationData);
        
        if (!result.success) {
            setErrors(result.errors);
            return false;
        }
        
        // Additional custom validations
        const newErrors: Record<string, string> = {};
        
        if (formData.type === 'scheduled') {
            if (!formData.scheduleTime) {
                newErrors.scheduleTime = 'Schedule time is required for scheduled jobs';
            }
            if (formData.scheduleFrequency === 'custom' && !formData.scheduleDate) {
                newErrors.scheduleDate = 'Schedule date is required for custom frequency';
            }
        }
        
        if (!isEditMode && !templateUploaded) {
            newErrors.template = 'Template file is required';
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        
        setErrors({});
        return true;
    };
    
    // Handle form submission
    const handleSubmit = async () => {
 
        
        if (!validateForm()) {
            return;
        }
        
        setSaving(true);
        setApiError(null);
        NProgress.start();
        
        try {
        
            
            if (isEditMode && jobId) {
                // Update existing job using EditJob API
                const updatePayload = {
                    jobId,
                    jobName: formData.name,
                    description: formData.description || ''
                };
                
                const response = await makeApiCall(API_PATHS.JOBS.EDIT, 'POST', updatePayload);
                setShowSuccessAlert(true);
                
                // Refresh job details
                if (response.job) {
                    setJobDetails(response.job);
                }
                
                // Redirect after success
                setTimeout(() => {
                    navigate(`/profile/${profileId}/jobs`);
                }, 2000);
            } else {
                // Create new job (existing logic - to be implemented with CreateJob API)
                setShowSuccessAlert(true);
                setTimeout(() => {
                    navigate(`/profile/${profileId}/jobs`);
                }, 2000);
            }
        } catch (error: any) {
            console.error('Error saving job:', error);
            setApiError(error.message || 'Failed to save job. Please try again.');
        } finally {
            setSaving(false);
            NProgress.done();
        }
    };
    
    // Handle cancel
    const handleCancel = () => {
        navigate(`/profile/${profileId}/jobs`);
    };
    
    // Handle run job
    const handleRunJob = async () => {
        if (!jobId) {
            console.error('No job ID available');
            return;
        }
        
        setIsRunningJob(true);
        setApiError(null);
        NProgress.start();
        
        try {
            
            const runJobPayload = {
                jobId,
                runType: 'NORMAL'
            };
            
            const response = await makeApiCall(API_PATHS.JOBS.RUN, 'POST', runJobPayload);
            
            // Show success message
            alert(`Job run started successfully! Run ID: ${response.runId || 'N/A'}`);
            
            // Navigate to job history to see the new run
            if (response.runId) {
                navigate(`/profile/${profileId}/job/${jobId}/history`);
            }
        } catch (error: any) {
            console.error('Error running job:', error);
            setApiError(error.message || 'Failed to run job. Please try again.');
        } finally {
            setIsRunningJob(false);
            NProgress.done();
        }
    };
    
    // Handle template download
    const handleDownloadTemplate = async () => {
        if (!jobDetails?.templateUri) {
            console.error('No template URI available');
            return;
        }
        
        try {
            NProgress.start();
            
            // Get presigned URL for the template
            const { getPresignedDownloadUrl } = await import('../../utils/s3Download');
            
            // Extract S3 key from URI
            const match = jobDetails.templateUri.match(/^s3:\/\/[^\/]+\/(.+)$/);
            if (!match || !match[1]) {
                throw new Error('Invalid S3 URI format');
            }
            
            const urlResponse = await getPresignedDownloadUrl(match[1]);
            
            if (!urlResponse?.downloadUrl) {
                throw new Error('Failed to get presigned URL for template');
            }
            
            // Fetch the template content
            const response = await fetch(urlResponse.downloadUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            
            // Create blob URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `template-${jobDetails.templateId || jobId || 'download'}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up blob URL
            window.URL.revokeObjectURL(blobUrl);
            
        } catch (error: any) {
            console.error('Error downloading template:', error);
            setApiError(`Failed to download template: ${error.message}`);
        } finally {
            NProgress.done();
        }
    };
    
    // Handle file upload
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setFormData({ ...formData, templateFile: file });
            setTemplateUploaded(true);
            setErrors({ ...errors, template: '' });
            // Load preview data for the uploaded file
            loadTemplatePreviewData();
        }
    };
    
    // Schedule frequency options
    const scheduleFrequencyOptions = [
        { label: 'Monthly', value: 'monthly' },
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Custom', value: 'custom' }
    ];
    
    return (
        <div className="job-create-edit-page" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ContentLayout
                defaultPadding={false}
                headerVariant="high-contrast"
                header={
                    <Box padding={{ horizontal: "xxxl", vertical: "l" }}>
                        <SpaceBetween size="m" direction="vertical">
                            <BreadcrumbGroup
                                items={[
                                    { text: "Home", href: "/" },
                                    { text: profileData?.name || 'Loading...', href: `/profile/${profileId}` },
                                    { text: "Jobs", href: `/profile/${profileId}/jobs` },
                                    { text: pageTitle, href: "#" }
                                ]}
                                ariaLabel="Breadcrumbs"
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                <div>
                                    <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                                        {pageTitle}
                                    </Box>
                                    <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                        {isEditMode && jobDetails ? (
                                            <>
                                                Created by: <Link 
                                                    href={`https://phonetool.amazon.com/users/${jobDetails.createdBy}`}
                                                    external={true}
                                                    fontSize="body-m"
                                                >
                                                    @{jobDetails.createdBy}
                                                </Link> | Created: {
                                                    new Date(jobDetails.createdAt).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })
                                                }
                                            </>
                                        ) : (
                                            `Profile: ${profileData?.name || 'Loading...'}`
                                        )}
                                    </Box>
                                </div>
                                {isEditMode && (
                                    <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                                        <Button
                                            iconName="refresh"
                                            variant="normal"
                                            onClick={fetchJobDetails}
                                            ariaLabel="Refresh job details"
                                            loading={loading}
                                        >
                                        
                                        </Button>
                                        <Button
                                            variant='primary'
                                            iconName="play"
                                            onClick={handleRunJob}
                                            loading={isRunningJob}
                                            disabled={(jobDetails?.status !== 'ACTIVE' && jobDetails?.status !== 'RELEASE') || isFeedbackGenerating || jobDetails?.templateStatus !== 'VALIDATED'}
                                        >
                                            Run Job
                                        </Button>
                                        <Button
                                            variant='normal'
                                            iconName="history"
                                            onClick={() => navigate(`/profile/${profileId}/job/${jobId}/history`)}
                                        >
                                            View Run History
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </SpaceBetween>
                    </Box>
                }
            >
                <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto', overflowX: 'hidden' }}>
                <Box padding={{ horizontal: "xxl", vertical: "l" }}>
                    {showSuccessAlert && (
                        <Alert
                            type="success"
                            dismissible
                            onDismiss={() => setShowSuccessAlert(false)}
                            header={isEditMode ? "Job updated successfully" : "Job created successfully"}
                        >
                            Redirecting to jobs list...
                        </Alert>
                    )}
                    
                    {apiError && (
                        <Alert
                            type="error"
                            dismissible
                            onDismiss={() => setApiError(null)}
                            header="Error"
                        >
                            {apiError}
                        </Alert>
                    )}
                    
                    {profileError && (
                        <Alert
                            type="warning"
                            dismissible
                            onDismiss={() => {}}
                            header="Profile Error"
                        >
                            {profileError}
                        </Alert>
                    )}
                    
                    {isEditMode && jobDetails?.templateStatus && jobDetails.templateStatus !== 'VALIDATED' && !isFeedbackGenerating && (
                        <Alert
                            type={jobDetails.templateStatus === 'GENERATION_FAILED' ? 'error' : 'info'}
                            header={jobDetails.templateStatus === 'GENERATION_FAILED' ? 'Template Generation Failed' : 'Template Not Ready'}
                        >
                            {jobDetails.templateStatus === 'GENERATION_FAILED' ? (
                                <>
                                    The template generation failed. Please use the feedback section below to provide more specific instructions and try again.
                                    The "Run Job" button is disabled until a valid template is generated.
                                </>
                            ) : (
                                <>
                                    The template for this job is currently in "{jobDetails.templateStatus}" status. 
                                    The "Run Job" button is disabled until the template is validated. 
                                    Please refresh to check for updated status.
                                </>
                            )}
                        </Alert>
                    )}
                  <Box padding={"xs"}> </Box>
                    
                    <SpaceBetween size="l" direction="vertical">
                        {/* Job Configuration */}
                        <Container
                            header={
                                <Header variant="h2">
                                    Job Configuration
                                </Header>
                            }
                        >
                            <SpaceBetween size="l" direction="vertical">
                                <FormField
                                    label="Name"
                                    errorText={errors.name}
                                    constraintText="Enter a unique name for this job"
                                >
                                    <Input
                                        value={formData.name}
                                        onChange={({ detail }) => {
                                            setFormData({ ...formData, name: detail.value });
                                            setErrors({ ...errors, name: '' });
                                        }}
                                        placeholder="Enter job name"
                                        ariaLabel="Job name"
                                    />
                                </FormField>
                                
                                <FormField
                                    label="Type"
                                    description="Currently only on-demand jobs are supported (scheduled jobs coming soon)"
                                >
                                    <RadioGroup
                                        onChange={({ detail }) => 
                                            setFormData({ ...formData, type: detail.value as 'ondemand' | 'scheduled' })
                                        }
                                        value={formData.type}
                                        items={[
                                            { value: 'ondemand', label: 'On demand' },
                                            { value: 'scheduled', label: 'Scheduled', disabled: true, description: 'Coming soon' }
                                        ]}
                                    />
                                </FormField>
                                
                                <FormField
                                    label="Description"
                                    description="Optional description for this job"
                                >
                                    <Textarea
                                        value={formData.description || ''}
                                        onChange={({ detail }) => 
                                            setFormData({ ...formData, description: detail.value })
                                        }
                                        placeholder="Enter job description"
                                        rows={3}
                                    />
                                </FormField>
                                
                                {/* <FormField
                                    label="Enable job"
                                    description="Toggle to enable or disable this job"
                                >
                                    <Toggle
                                        onChange={({ detail }) =>
                                            setFormData({ ...formData, enabled: detail.checked })
                                        }
                                        checked={formData.enabled}
                                    >
                                        {formData.enabled ? 'Enabled' : 'Disabled'}
                                    </Toggle>
                                </FormField> */}
                                
                                {/* Form Actions */}
                                <Box float="right">
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button onClick={handleCancel}>
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSubmit}
                                            loading={saving}
                                        >
                                            {isEditMode ? 'Update' : 'Create'}
                                        </Button>
                                    </SpaceBetween>
                                </Box>
                            </SpaceBetween>
                        </Container>
                        
                        {/* Feedback Section - Show in edit mode when there's a template OR when generation failed (to allow retry) */}
                        {isEditMode && (templateUploaded || jobDetails?.templateStatus === 'GENERATION_FAILED') && jobDetails?.templateStatus !== 'GENERATING' && (
                            <Container
                                id="feedback-section"
                                header={
                                    <Header
                                        variant="h2"
                                        description="Provide feedback to improve the template generation"
                                        actions={
                                            isFeedbackGenerating && (
                                                <Badge color="blue">
                                                    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                                                        <Spinner />
                                                        <span>Generating</span>
                                                    </SpaceBetween>
                                                </Badge>
                                            )
                                        }
                                    >
                                        Template Feedback
                                    </Header>
                                }
                            >
                                <SpaceBetween size="m">
                                    {/* Status Messages */}
                                    {feedbackSuccessMessage && (
                                        <Alert
                                            type="success"
                                            dismissible
                                            onDismiss={clearFeedbackSuccessMessage}
                                            header="Template Updated Successfully"
                                        >
                                            {feedbackSuccessMessage}
                                        </Alert>
                                    )}
                                    
                                    {feedbackError && (
                                        <Alert
                                            type="error"
                                            dismissible
                                            onDismiss={clearFeedbackError}
                                            header="Template Generation Failed"
                                        >
                                            <SpaceBetween size="s">
                                                {/* Render error message with proper formatting for newlines and bullets */}
                                                <Box>
                                                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                                        {feedbackError.split('\n').map((line, index) => {
                                                            // Check if line starts with "- " (bullet point)
                                                            if (line.trim().startsWith('- ')) {
                                                                return (
                                                                    <div key={index} style={{ paddingLeft: '16px', marginBottom: '4px' }}>
                                                                        • {line.trim().substring(2)}
                                                                    </div>
                                                                );
                                                            }
                                                            // Check if line ends with ":" (section header)
                                                            if (line.trim().endsWith(':') && line.trim().length > 1) {
                                                                return (
                                                                    <div key={index} style={{ fontWeight: 600, marginTop: index > 0 ? '12px' : '0', marginBottom: '4px' }}>
                                                                        {line}
                                                                    </div>
                                                                );
                                                            }
                                                            // Regular line
                                                            return line.trim() ? (
                                                                <div key={index} style={{ marginBottom: '4px' }}>
                                                                    {line}
                                                                </div>
                                                            ) : (
                                                                <div key={index} style={{ height: '8px' }} />
                                                            );
                                                        })}
                                                    </div>
                                                </Box>
                                            </SpaceBetween>
                                        </Alert>
                                    )}
                                    
                                    {/* Generation Progress */}
                                    {isFeedbackGenerating && (
                                        <Alert
                                            type="info"
                                            header="Template Generation in Progress"
                                        >
                                            <SpaceBetween size="xs">
                                                <Box>{getFeedbackStatusMessage(feedbackGenerationStatus)}</Box>
                                                <ProgressBar
                                                    status="in-progress"
                                                    value={generationProgress}
                                                    label={`Processing feedback`}
                                                   
                                                />
                                            </SpaceBetween>
                                        </Alert>
                                    )}
                                    
                                    {/* Feedback Form */}
                                    <FormField
                                        label="Provide Feedback"
                                        description={`Describe what improvements you'd like to see in the template (max 300 words, current: ${wordCount})`}
                                        errorText={wordCount > 300 ? 'Feedback exceeds 300 words limit' : ''}
                                        stretch
                                    >
                                        <Textarea
                                            value={feedbackText}
                                            onChange={({ detail }) => setFeedbackText(detail.value)}
                                            placeholder="Example: The template should include additional error handling for edge cases..."
                                            rows={6}
                                            disabled={isFeedbackGenerating}
                                            ariaLabel="Feedback text"
                                        />
                                    </FormField>
                                    
                                    {/* Character/Word Counter and Submit Buttons */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box fontSize="body-s" color={wordCount > 300 ? "text-status-error" : "text-label"}>
                                            <strong>{wordCount}/300</strong> words
                                        </Box>
                                        <SpaceBetween direction="horizontal" size="xs">
                                            <Button
                                                variant="link"
                                                onClick={() => {
                                                    setFeedbackText('');
                                                    clearFeedbackError();
                                                    clearFeedbackSuccessMessage();
                                                }}
                                                disabled={isFeedbackGenerating || !feedbackText.trim()}
                                            >
                                                Clear
                                            </Button>
                                            <Button
                                                variant="primary"
                                                onClick={submitFeedback}
                                                disabled={!feedbackText.trim() || isFeedbackGenerating || wordCount > 300 || wordCount === 0}
                                                loading={isFeedbackGenerating}
                                                iconName="upload"
                                            >
                                                {isFeedbackGenerating ? 'Generating Template...' : 'Submit Feedback'}
                                            </Button>
                                        </SpaceBetween>
                                    </div>
                                    
                                    {/* Information Box */}
                                    <Alert type="info">
                                        <Box>
                                            <strong>How feedback works:</strong>
                                            <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                                                <li>Your feedback will be used to regenerate an improved template</li>
                                                <li>The template generation process typically takes few minutes</li>
                                                <li>Once validated, the job will use the updated template for future runs</li>
                                                <li>You can provide multiple rounds of feedback to refine the template</li>
                                                <li>Run again from this page to use the updated template and get the latest results</li>
                                            </ul>
                                        </Box>
                                    </Alert>
                                </SpaceBetween>
                            </Container>
                        )}
                        
                        {/* Template Section */}
                        {/* Show info when template is being generated */}
                        {(jobDetails?.templateStatus === 'SUBMITTED' || jobDetails?.templateStatus === 'GENERATING' || isFeedbackGenerating) && (
                            <Alert
                                type="info"
                                header="Template Generation in Progress"
                            >
                                The template section is temporarily unavailable while the template is being generated. 
                                {isFeedbackGenerating ? 'Your feedback is being processed.' : 'Please refresh the page to check if the generation has completed.'}
                            </Alert>
                        )}
                        
                        {/* Template Section - Hide when template is being generated or submitted, or when feedback is being processed */}
                        {jobDetails?.templateStatus !== 'SUBMITTED' && jobDetails?.templateStatus !== 'GENERATING' && !isFeedbackGenerating && (
                        <Container
                            header={
                                <Header
                                    variant="h2"
                                    actions={
                                        <SpaceBetween direction="horizontal" size="xs">
                                            <Button
                                                iconName="download"
                                                onClick={handleDownloadTemplate}
                                                disabled={!templateUploaded}
                                            >
                                                Download
                                            </Button>
                                        </SpaceBetween>
                                    }
                                >
                                    Template on which the job will be created
                                </Header>
                            }
                        >
                                {templateUploaded && templatePreviewData ? (
                                    <JsonDataViewer
                                        data={templatePreviewData}
                                        mermaidDiagram={jobDetails?.mermaidDiagram}
                                        aiSummary={jobDetails?.aiSummary}
                                        defaultView="grid"
                                    style={{ 
                                            maxHeight: '500px'
                                    }}
                                        collapsed={false}
                                            displayObjectSize={false}
                                        enableClipboard={true}
                                    />
                            ) : (
                                <Box textAlign="center" padding="xl" color="text-status-inactive">
                                    <SpaceBetween size="s" direction="vertical">
                                        <Icon name="file" size="large" />
                                        <Box>
                                            {isEditMode ? (
                                                'Template preview will appear here once loaded'
                                            ) : (
                                                'No template uploaded yet'
                                            )}
                                        </Box>
                                    </SpaceBetween>
                                </Box>
                            )}
                        </Container>
                        )}
                    </SpaceBetween>
                </Box>
                {/* Add spacing at bottom to prevent footer overlap */}
                <Box padding={{ bottom: "xxxl" }}></Box>
                </div>
            </ContentLayout>
        </div>
    );
};

export default JobCreateEdit;
